/**
 * Visible FAQ section for landing-shell pages.
 *
 * What: Renders question-and-answer cards that mirror the FAQ structured data.
 * Why: Provides a visible, crawlable FAQ so schema and page content stay aligned.
 * When: Update when the FAQ card layout changes (copy lives with each page's
 * content builder).
 */
import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

import LandingCenteredSectionHead from '@/components/landing/LandingCenteredSectionHead';
import { landingSectionIds } from '@/components/landing/landingContent';
import LandingSection from '@/components/landing/LandingSection';
import type { LandingSectionTone } from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens.js';

export interface FaqItem {
  question: string;
  answer: string;
}

interface LandingFaqSectionProps {
  id?: string;
  tone?: LandingSectionTone;
  content: FaqItem[];
  /** 'center' matches the homepage design; 'start' keeps level/school layouts. */
  align?: 'start' | 'center';
}

function LandingFaqSection({
  content,
  id = landingSectionIds.faq,
  tone = 'transparent',
  align = 'start',
}: LandingFaqSectionProps) {
  const titleId = `${id}-title`;
  const centered = align === 'center';

  return (
    <LandingSection id={id} tone={tone} ariaLabelledby={titleId}>
      <Stack spacing={centered ? 0 : { xs: 3, sm: 3.5 }}>
        {centered ? (
          <LandingCenteredSectionHead
            titleId={titleId}
            eyebrow="Frequently asked questions"
            title="Clear answers for parents"
          />
        ) : (
          <Stack spacing={1.25} sx={{ maxWidth: { xs: '100%', sm: 760 } }}>
            <Typography
              variant="overline"
              sx={{
                letterSpacing: 2,
                color: colors.accentCobalt,
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              Frequently asked questions
            </Typography>
            <Typography
              id={titleId}
              component="h2"
              sx={{
                fontSize: { xs: 28, sm: 32, md: 36 },
                fontWeight: 800,
                letterSpacing: -0.5,
                color: colors.ink,
                lineHeight: 1.15,
              }}
            >
              Clear answers for parents
            </Typography>
          </Stack>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: { xs: 2, sm: 2 },
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, minmax(0, 1fr))',
            },
          }}
        >
          {content.map((item: FaqItem) => (
            <Paper
              key={item.question}
              elevation={0}
              component="article"
              sx={{
                p: '20px 22px',
                borderRadius: radii.md,
                backgroundColor: colors.paper,
                border: `1px solid ${colors.border}`,
                boxShadow: shadows.soft,
              }}
            >
              <Stack spacing={1.125}>
                <Typography
                  component="h3"
                  sx={{
                    color: colors.ink,
                    fontWeight: 800,
                    fontSize: { xs: 14.5, sm: 15.5 },
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                  }}
                >
                  {item.question}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.inkMuted,
                    lineHeight: 1.62,
                    fontSize: { xs: 13, sm: 13.5 },
                    fontWeight: 500,
                  }}
                >
                  {item.answer}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Box>
      </Stack>
    </LandingSection>
  );
}

export default LandingFaqSection;
